import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Tabs, Empty, Row, Button, Space, message, Upload } from "antd";
import { useForm, useFieldArray } from "react-hook-form";
import { intersection } from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import readXlsxFile from "read-excel-file";
import { CardElement } from "../../components/Common";
import { REPORT_TYPES, getAvailableDocTypes, preProcessReportExcelData } from "../../utils/report";
import AdditionalReportsTopBar from "../../components/Report/AdditionalReportsTopBar";
import AdditionalReportsTable from "../../components/Report/AdditionalReportsTable";
import { additionalReportsSchema } from "../../schema/report.schema";
import {
  useGetLMDDocumentTypes,
  useGetProject,
  useGetProjectHotels,
  useGetProjectReportCategories,
  useReportCreateReportList,
} from "../../hooks";
import { ONQ, PMS_ALL_VALUE } from "../../utils/pms.utils";

const AdditionalReports = (props) => {
  const [hmg, setHmg] = useState();
  const [project, setProject] = useState();
  const [pms, setPMS] = useState();
  const [hotels, setHotels] = useState([]);
  const [hotelListData, setHotelListData] = useState([]);
  const [pmsListData, setPMSListData] = useState([]);
  const [frequency, setFrequency] = useState([]);
  const [fileList, setFileList] = useState();

  const setHmgRef = useCallback((list) => {
    if (list?.length === 1) setHmg(list[0]._id);
  }, []);
  const setProjectRef = useCallback((list) => {
    if (list?.length === 1) setProject(list[0]._id);
  }, []);
  const setPMSRef = useCallback((list) => {
    if (list?.length === 1) setPMS(list[0]._id);
    setPMSListData(list);
  }, []);

  const setHotelListRef = useCallback((list) => {
    if (list?.length === 1) setHotels([list[0]._id]);
    setHotelListData(list);
  }, []);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    resetField,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(additionalReportsSchema),
  });

  const fieldArrayProps = useFieldArray({
    control,
    name: "formData",
  });

  useEffect(() => {
    reset();
  }, [hotels]);

  const tableFields = useMemo(() => {
    return fieldArrayProps.fields.map((field, index) => ({
      ...field,
      rowIndex: index,
    }));
  }, [fieldArrayProps.fields]);

  const pmsName = useMemo(() => {
    if (pms === PMS_ALL_VALUE) {
      return pms;
    }
    const selectedPmsRecord = pmsListData.find(
      (pmsRecord) => pmsRecord._id === pms,
    );
    return selectedPmsRecord?.pms_name;
  }, [pms]);

  const { data: projectData } = useGetProject(project, {
    enabled: !!project,
  });

  const { data: reportCategories } = useGetProjectReportCategories(project, {
    enabled: !!project,
  });
  const { data: documentTypes } = useGetLMDDocumentTypes();
  const { data: projectHotels } = useGetProjectHotels(project, {
    enabled: !!project,
  });

  const { mutateAsync: createReports, isLoading: isProcessingReports } =
    useReportCreateReportList({
      onSuccess: (data) => {
        const selectedHotel = hotelListData.find(
          (hotel) => hotel._id === data?.[0].hotel_id,
        );
        const hotelName = selectedHotel?.hotel_name
          ? `${selectedHotel?.hotel_name}: `
          : "";
        message.success(`${hotelName}Reports Added Successfully`);
        fieldArrayProps.remove();
        setFrequency([]);
      },
      onError: (error) => {
        // const selectedHotel = hotelListData.find(
        //   (hotel) => hotel._id === data?.[0].hotel_id,
        // );
        // const hotelName = selectedHotel?.hotel_name
        //   ? `${selectedHotel?.hotel_name}: `
        //   : "";
        // const errorMessage =
        //   hotelName + ();
        message.error(error?.response?.data?.message ?? "Server Error");
      },
    });

    const availableDocTypes = useMemo(
      () =>
        getAvailableDocTypes({
          hotelData: projectHotels ?? [],
          selectedHotels: hotels,
          documentTypes: documentTypes ?? [],
        }),
      [projectHotels, hotels, documentTypes],
    );

  const errorHandler = (errorSet) => {
    const nonMandatoryErrors = [
      "case-insensitive-report-name-exist",
      "is-valid-deactivation-date",
      "is-sa-date-after-activation-date",
      "is-sa-date-before-deactivation-date",
      "is-sd-date-after-activation-date",
      "is-sd-date-before-deactivation-date",
      "is-sd-date-before-sa-date",
    ];
    let mandatoryError;
    let nonMandatoryError;
    errorSet.formData.forEach((record) => {
      const flattedErrorList = Object.values(record);
      mandatoryError = flattedErrorList.find(
        (errorObj) => !nonMandatoryErrors.includes(errorObj.type),
      );
      nonMandatoryError = flattedErrorList.find((errorObj) =>
        nonMandatoryErrors.includes(errorObj.type),
      );
    });
    if (mandatoryError) {
      message.error("Please fill in the mandatory field(s)");
    } else if (nonMandatoryError) {
      message.error(nonMandatoryError.message);
    }
  };

  const submitFn = async (data) => {
    for (let index = 0; index < hotels.length; index++) {
      const selectedHotel = hotels[index];
      const processData = data.formData.map((el) => {
        return {
          ...el,
          project_id: project,
          hotel_id: selectedHotel,
        };
      });
      await createReports(processData);
    }
    // setHmg();
  };

  //excel upload
  const hadleFileChange = (info) => {
    let fileList = [...info.fileList];
    fileList = fileList.slice(-1);
    fileList = fileList.map((file) => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });
    setFileList(fileList);
  };

  const beforeUpload = async (event) => {
    const fileObj = event;
    let reportset = [];

    try {
      await Promise.all(
        REPORT_TYPES.filter((type) => frequency.includes(type.ReportType)).map(
          async (el) => {
            const data = await readXlsxFile(fileObj, {
              sheet: el.ReportType,
            });
            reportset = [
              ...reportset,
              ...preProcessReportExcelData(data, el.ReportType, {
                reportCategories,
                documentTypes: availableDocTypes,
                projectSolution: projectData?.solution,
              }),
            ];
          },
        ),
      );
    } catch (error) {
      console.log(error, "file upload error");
      message.error("unable to read the file");
    } finally {
      fieldArrayProps.replace(reportset);
    }
    return false;
  };

  const setFrequencyFn = (e) => {
    const removedFrequencyReportIndexes = [];
    fieldArrayProps.fields.forEach((report, index) => {
      if (!e.includes(report.shedule_type)) {
        removedFrequencyReportIndexes.push(index);
      }
    });
    fieldArrayProps.remove(removedFrequencyReportIndexes);
    setFrequency(e);
  };

  return (
    <CardElement title="Advanced Report Management - Add Additional Reports">
      <AdditionalReportsTopBar
        hmg={hmg}
        setHmg={setHmg}
        setHmgRef={setHmgRef}
        project={project}
        setProject={setProject}
        setProjectRef={setProjectRef}
        pms={pms}
        setPMS={setPMS}
        setPMSRef={setPMSRef}
        hotel={hotels}
        setHotel={setHotels}
        setHotelListRef={setHotelListRef}
        frequency={frequency}
        setFrequency={setFrequencyFn}
        // dataRetrievalFn={() => {}}
        // isLoading={false}
      />
      {frequency.length > 0 ? (
        <>
          <Space direction="vertical">
            <Button
              href="/excel-template/Report_Template.xlsx?v1.0"
              type="primary"
              icon={<DownloadOutlined />}
              size="medium"
              shape="round"
            >
              Download Template
            </Button>
            <Upload
              beforeUpload={(event) => {
                beforeUpload(event);
                return false;
              }}
              onChange={hadleFileChange}
              accept=".xlsx"
              name="file"
              fileList={fileList}
            >
              <Button>
                <UploadOutlined /> Click to Upload
              </Button>
            </Upload>
          </Space>
          <Tabs
            defaultActiveKey="1"
            items={REPORT_TYPES.filter((type) =>
              frequency.includes(type.ReportType),
            ).map((elm) => {
              const reportType = elm.ReportType;
              return {
                label: reportType,
                key: reportType,
                children: (
                  <AdditionalReportsTable
                    formRelatedProps={{
                      control,
                      watch,
                      reset,
                      resetField,
                      setValue,
                      getValues,
                      errors,
                    }}
                    fieldArrayProps={{
                      ...fieldArrayProps,
                      fields: tableFields,
                    }}
                    type={reportType}
                    project={project}
                    hotels={hotels}
                    projectSolution={projectData?.solution}
                    availableDocTypes={availableDocTypes}
                    selectedPmsLabel={pmsName}
                  />
                ),
              };
            })}
          />
          {fieldArrayProps.fields.length > 0 && (
            <Row justify="center" gutter={[8, 8]}>
              <Space>
                <Button onClick={() => setHmg()}>Cancel</Button>
                <Button
                  type="primary"
                  disabled={fieldArrayProps.fields.length === 0}
                  onClick={handleSubmit(submitFn, errorHandler)}
                  loading={isProcessingReports}
                >
                  Save
                </Button>
              </Space>
            </Row>
          )}
        </>
      ) : (
        <Empty
          description={
            <span>
              Please select the <strong>Filter</strong> to add additional
              Reports
            </span>
          }
        />
      )}
    </CardElement>
  );
};

AdditionalReports.propTypes = {};

export default AdditionalReports;
